import React, { useEffect, useState } from 'react'
import TeamOdd from './TeamOdd.tsx';
import { useUser } from '../../../../context/UserContext.tsx';
import { increment, Timestamp } from 'firebase/firestore';
import { generateUniqueTicket, getPendingBetsForPlacement, placeAllWagers } from '../../../../services/player/BetService.tsx';
import { editUser, logUserActivity } from '../../../../services/player/LoginService.tsx';
import { useBetContext } from '../../../../context/ParlayBetContext.tsx';
import { BetDetails } from '../../../../models/MyBetModel.tsx';
import { useModal } from '../../../../context/ModalContext.tsx';
import { dataUser } from '../../../../services/player/apis/Schelude.tsx';
import { fetchOddsSnapshotsByFixtureIds } from '../../../../helpers/oddsSnapshotCache.tsx';
import { exceedsGainLimit, formatGainLimitDisplay, formatNumber, getMultiLegBetSignature, shouldShowDelayedBetMessage, syncTeaserOddName } from '../../../../helpers/functions.tsx';
import WagerConfirmedSlip from '../WagerConfirmedSlip.tsx';
import { getAgentMaxPayout } from '../../../../services/player/SportService.tsx';
import { BetSlipActions, BetSlipChrome, BetSlipStake } from '../BetSlipChrome.tsx';
import '../../../../styles/StraightQuickSlip.css';

function formatTeaserSport(bets: { sport?: string | null }[]) {
    const text = String(bets.find((bet) => bet.sport)?.sport ?? '').trim();
    if (!text) return '';
    return text.replace(/\b([A-Za-z0-9]+)\b/g, (word) => {
        if (word.length <= 4 && word === word.toUpperCase()) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
}

interface TeaserBetsProps {
    setShowLimitAlert: (value: boolean) => void;
    setErrorMessage: (value: string) => void;
}

const TeaserBets = (props: TeaserBetsProps) => {
    const { setShowLimitAlert, setErrorMessage } = props;

    const teaserPayoutTable = {
        "3TeamTiesLose": {
            1: { 7: -130 },
            2: { 7: -130 },
            3: { 7: -130 },
        },
        "4TeamTiesLose": {
            1: { 10: -140 },
            2: { 10: -140 },
            3: { 10: -140 },
            4: { 10: -140 },
        },
        "classic": {
            1: { 4: -120 },
            2: { 4: -120 },
            3: { 4: 150 },
            4: { 4: 235 },
            5: { 4: 350 },
            6: { 4: 550 },
            7: { 4: 800 },
        },
        "classic0.5": {
            1: { 4.5: -130 },
            2: { 4.5: -130 },
            3: { 4.5: 135 },
            4: { 4.5: 215 },
            5: { 4.5: 320 },
            6: { 4.5: 500 },
            7: { 4.5: 700 },

        },
        "classic1": {
            1: { 5: -140 },
            2: { 5: -140 },
            3: { 5: 120 },
            4: { 5: 200 },
            5: { 5: 300 },
            6: { 5: 475 },
            7: { 5: 600 },
        },
    };

    const teaserLimits = {
        '3TeamTiesLose': { min: 3, max: 3 },
        '4TeamTiesLose': { min: 4, max: 4 },
        'classic': { min: 2, max: 7 },
        'classic0.5': { min: 2, max: 7 },
        'classic1': { min: 2, max: 7 },
    };

    const { bets, deleteAllBets, teaserPoints, teaserType } = useBetContext();
    const { user, updateUser, userIpData } = useUser();
    const [totalBet, setTotalBet] = useState("");
    const [totalReward, setTotalReward] = useState(0);
    const numberOfTeams = bets.length;
    const [userBet, setUserBet] = useState<(BetDetails)[]>([]);
    const [showPendingOnly, setShowPendingOnly] = useState(false);
    const [ticketNumber, setTicketNumber] = useState<number | null>(null);
    const [confirmedOdds, setConfirmedOdds] = useState<string | null>(null);
    const [confirmedDetail, setConfirmedDetail] = useState<string | null>(null);
    const sport = bets[0]?.sport;
    const { closeModal } = useModal();
    const [placingBet, setPlacingBet] = useState(false);
    const shouldShowDelayedMessage = bets.some(bet => shouldShowDelayedBetMessage(bet));

    useEffect(() => {
        const numericValue = totalBet === '' ? 0 : parseFloat(totalBet);
        setTotalReward(
            calculateTeaserReward(numericValue, numberOfTeams, teaserPoints, teaserType)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bets, teaserPoints, teaserType]);

    const handleTotalBetChange = (event: any) => {
        let value = event.target.value;

        if (value.startsWith('0') && value.length > 1 && !value.startsWith('0.')) {
            value = value.replace(/^0+/, '');
        }

        if (/^\d*\.?\d*$/.test(value)) {
            setTotalBet(value);
            const numericValue = value === '' ? 0 : parseFloat(value);
            setTotalReward(
                calculateTeaserReward(numericValue, numberOfTeams, teaserPoints, teaserType)
            );
        }
    };

    const calculateTeaserReward = (
        betAmount: number,
        numTeams: number,
        points: number,
        teaserType: string
    ) => {
        const payout = teaserPayoutTable[teaserType]?.[numTeams]?.[points];
        if (!payout) return 0;

        let reward = 0;

        if (payout < 0) {
            reward = (100 / Math.abs(payout)) * betAmount;
        } else {
            reward = (payout / 100) * betAmount;
        }

        return Math.round(reward);
    };

    const confirmedBet = async () => {
        if (placingBet) return;
        setPlacingBet(true);

        const limits = teaserLimits[teaserType];

        if (!limits) {
            setShowLimitAlert(true);
            setErrorMessage('Invalid teaser type or limits not defined.');
            setPlacingBet(false);
            return;
        }
        if (teaserType === '3TeamTiesLose' && numberOfTeams !== 3) {
            setShowLimitAlert(true);
            setErrorMessage('You must have exactly 3 selections for this type of teaser.');
            setPlacingBet(false);

            return;
        }
        if (teaserType === '4TeamTiesLose' && numberOfTeams !== 4) {
            setShowLimitAlert(true);
            setErrorMessage('You must have exactly 4 selections for this type of teaser.');
            setPlacingBet(false);
            return;
        }

        if (numberOfTeams < limits.min || numberOfTeams > limits.max) {
            setShowLimitAlert(true);
            setErrorMessage(`You must have between ${limits.min} and ${limits.max} selections for this type of teaser.`);
            setPlacingBet(false);
            return;
        }

        // const betAmount = parseFloat(totalBet);
        const [userData, agentMaxPayout] = await Promise.all([
            dataUser(user?.id ?? ""),
            getAgentMaxPayout(user ?? {}),
        ]);
        const betDate = Timestamp.fromDate(new Date());
        const userId = user?.id;
        const numericTotalBet = parseFloat(totalBet) || 0;
        const formattedReward = Number(totalReward.toFixed(2));
        const userAvailable = userData?.available;
        const teaserMaxWager = Number(userData?.teaserMaxWager ?? user?.teaserMaxWager) || 0;

        if (totalBet === "" || numericTotalBet < 10 || formattedReward < 10) {
            setShowLimitAlert(true);
            setErrorMessage('Both the bet amount and the potential win must be at least $10.');
            setPlacingBet(false);
            return;
        }

        if (teaserMaxWager > 0 && numericTotalBet > teaserMaxWager) {
            setShowLimitAlert(true);
            setErrorMessage(`Your risk of ${numericTotalBet.toFixed(2)} exceeds the wager limit of ${teaserMaxWager}.`);
            setPlacingBet(false);
            return;
        }

        if (teaserMaxWager > 0 && formattedReward > teaserMaxWager) {
            setShowLimitAlert(true);
            setErrorMessage(`Your to win of ${formattedReward.toFixed(2)} exceeds the max to win of ${teaserMaxWager}.`);
            setPlacingBet(false);
            return;
        }

        if (exceedsGainLimit(formattedReward, agentMaxPayout)) {
            setShowLimitAlert(true);
            setErrorMessage(`Your potential win of ${formattedReward.toFixed(2)} exceeds the max payout limit of ${formatGainLimitDisplay(agentMaxPayout)}.`);
            setPlacingBet(false);
            return;
        }

        if (numericTotalBet > userAvailable) {
            setShowLimitAlert(true);
            setErrorMessage('Insufficient available for the total bet.');
            setPlacingBet(false);
            return;
        }

        const uniqueFixtureIds = [...new Set(
            bets.map(b => b.fixture_id).filter(Boolean).map(id => String(id).trim())
        )];
        const uniqueGameIds = [...new Set(
            bets.map(b => b.gameId).filter(Boolean).map(id => String(id).trim())
        )];
        const rootFixtureId = uniqueFixtureIds[0] || "";
        const rootGameId = uniqueGameIds[0] || "";

        let pendingTeasers: BetDetails[] = [];
        try {
            const lookupBets = uniqueFixtureIds.map(fixture_id => ({
                fixture_id,
                gameId: rootGameId,
                betType: "Teaser",
            })) as BetDetails[];

            const [gamesByFixture, allPending] = await Promise.all([
                fetchOddsSnapshotsByFixtureIds(uniqueFixtureIds),
                getPendingBetsForPlacement(user?.userName || userData?.userName || '', lookupBets, { includeTeasers: true }),
            ]);

            const incomingSignature = getMultiLegBetSignature(bets);

            pendingTeasers = allPending.filter(b => {
                if (String(b.betType ?? "").toLowerCase() !== "teaser") return false;
                // Solo acumular apuestas idénticas
                return getMultiLegBetSignature(b.selections) === incomingSignature;
            });

            for (const fixtureId of uniqueFixtureIds) {
                const game = gamesByFixture.get(fixtureId)?.[0];
                const isInvalidGame = !game || game.is_live || game.status === "live";

                if (isInvalidGame) {
                    setShowLimitAlert(true);
                    setErrorMessage(
                        game
                            ? `The game ${game.away_team_display} vs ${game.home_team_display} has already started. This bet is closed.`
                            : `One of the games in your teaser is no longer available. This bet is closed.`
                    );
                    setPlacingBet(false);
                    return;
                }
            }
        } catch (error) {
            console.error(error);
            setShowLimitAlert(true);
            setErrorMessage('Unable to validate pending bets. Please try again.');
            setPlacingBet(false);
            return;
        }

        const totalExistingRisk = pendingTeasers.reduce(
            (sum, p) => sum + Number(p.bet ?? 0),
            0
        );
        const totalExistingReward = pendingTeasers.reduce(
            (sum, p) => sum + Number(p.reward ?? 0),
            0
        );
        const totalRiskAfter = totalExistingRisk + numericTotalBet;
        const totalRewardAfter = totalExistingReward + formattedReward;

        if (teaserMaxWager > 0 && totalRiskAfter > teaserMaxWager) {
            setShowLimitAlert(true);
            setErrorMessage(
                `Limit exceeded. You already have $${totalExistingRisk.toFixed(2)} risk on these identical teasers, maximum wager allowed is $${teaserMaxWager}.`
            );
            setPlacingBet(false);
            return;
        }

        if (teaserMaxWager > 0 && totalRewardAfter > teaserMaxWager) {
            setShowLimitAlert(true);
            setErrorMessage(
                `Limit exceeded. You already have $${totalExistingReward.toFixed(2)} to win on these identical teasers, maximum to win allowed is $${teaserMaxWager}.`
            );
            setPlacingBet(false);
            return;
        }

        if (exceedsGainLimit(totalRewardAfter, agentMaxPayout)) {
            setShowLimitAlert(true);
            setErrorMessage(
                `Limit exceeded. You already have $${totalExistingReward.toFixed(2)} on these identical teasers.\n` +
                `Maximum gain allowed is ${formatGainLimitDisplay(agentMaxPayout)}.`
            );
            setPlacingBet(false);
            return;
        }

        const ticketBase = await generateUniqueTicket();
        setTicketNumber(ticketBase);

        const updatedBets = bets.map((bet, index) => {
            const odd = bet.odd ? syncTeaserOddName(bet.odd) : bet.odd;
            return {
                ...bet,
                odd,
                betName: odd?.name ?? bet.betName,
                ticket: `${ticketBase}-${index + 1}`,
            };
        });

        const teaserDetails: BetDetails = {
            bet: numericTotalBet,
            reward: formattedReward,
            betStatus: 'pending',
            betDate: betDate,
            betType: 'Teaser',
            description: `Teaser - ${bets.length} Teams ${teaserPoints}Pts ${sport}`,
            gameDate: bets.length > 0 && bets[0].gameDate ? bets[0].gameDate : Timestamp.fromDate(new Date()),
            selections: updatedBets,
            fixture_id: rootFixtureId,
            gameId: rootGameId,
            player: {
                userDocId: userId,
                userName: userData?.userName,
                parent: userData?.parent,
                pin: userData?.pin,
                name: userData?.name,
                img: userData?.img,
                master: userData?.master ?? 'CJ223'
            },
            ticket: `${ticketBase}`
        };

        try {

            await placeAllWagers([teaserDetails]);
            await editUser(userData.id!, {
                available: increment(-numericTotalBet),
                pending: increment(numericTotalBet),
            });

            const newAvailable = userAvailable - numericTotalBet;
            const newPending = (Number(userData?.pending ?? user?.pending) || 0) + numericTotalBet;
            updateUser({ available: newAvailable, pending: newPending });

            logUserActivity(
                userData?.userName,
                `Ticket: ${ticketBase} Risk: $${numericTotalBet.toFixed(2)} Win: $${formattedReward.toFixed(2)}`, userIpData
            );
            const payout = teaserPayoutTable[teaserType]?.[bets.length]?.[teaserPoints];
            setConfirmedOdds(payout != null ? formatNumber(payout) : null);
            setConfirmedDetail(teaserPoints != null ? `${teaserPoints} Point Teaser` : null);
            setUserBet([teaserDetails]);
            setShowPendingOnly(true);
            deleteAllBets();
            setTotalBet("");
            setTotalReward(0);

        } catch (error) {
            setShowLimitAlert(true);
            setErrorMessage('There was an error confirming the bet. Please try again.');
        } finally {
            setPlacingBet(false);
        }
    };

    const resetBetState = () => {
        setShowPendingOnly(false);
        setTicketNumber(null);
        setConfirmedOdds(null);
        setConfirmedDetail(null);
        setUserBet([])
        closeModal();
        deleteAllBets();
        setTotalBet("");
        setTotalReward(0);
    };

    const bet = userBet.reduce((accumulator, currentValue) => accumulator + Number(currentValue.bet), 0);
    const reward = userBet.reduce((accumulator, currentValue) => accumulator + (currentValue.reward ?? 0), 0);

    const closeTicket = () => {
        setShowPendingOnly(false);
        setTicketNumber(null);
        setUserBet([]);
        closeModal();
    }

    useEffect(() => {
        if (showPendingOnly || placingBet) return;
        if (bets.length === 0) closeModal();
    }, [bets.length, showPendingOnly, placingBet, closeModal]);
    // console.log(bets)


    const teaserPayout = teaserPayoutTable[teaserType]?.[numberOfTeams]?.[teaserPoints];
    const teaserOddsLabel = teaserPayout != null ? `TEASER ODDS ${formatNumber(teaserPayout)}` : '';

    if (showPendingOnly) {
        const legs = userBet.flatMap((item) => (item.selections?.length ? item.selections : [item]));
        return (
            <div className="bet-slip bet-slip--teaser">
                <WagerConfirmedSlip
                    legs={legs}
                    risk={bet}
                    win={reward}
                    ticketNumber={ticketNumber}
                    onClose={resetBetState}
                    kind="teaser"
                    oddsLabel={confirmedOdds}
                    detailLabel={confirmedDetail}
                />
            </div>
        );
    }

    return (
        <div className="bet-slip bet-slip--teaser">
            <BetSlipChrome
                pickCount={showPendingOnly ? userBet.length : bets.length}
                mode="teaser"
                onClose={closeTicket}
                hideTabs
            />
            {!showPendingOnly && (
                <>
                    <div className="bet-slip__scroll">
                    <div className="sq-confirm__group parlay-slip-group">
                        <div className="sq-confirm__group-head">
                            <div className="sq-confirm__group-copy">
                                <span className="sq-confirm__group-title">
                                    {[`${bets.length}-Team`, formatTeaserSport(bets), 'Teaser'].filter(Boolean).join(' ')}
                                </span>
                                <span className="sq-confirm__group-sub">
                                    {teaserPoints != null ? `${teaserPoints} Point Teaser` : 'Teaser'} • {bets.length} {bets.length === 1 ? 'Selection' : 'Selections'}
                                </span>
                            </div>
                            <span className="sq-confirm__group-picks">
                                {bets.length} {bets.length === 1 ? 'Pick' : 'Picks'}
                            </span>
                        </div>
                        <div className="sq-confirm__group-legs">
                            {bets.map((selection, i) => (
                                <TeamOdd bet={selection} key={i} />
                            ))}
                        </div>
                    </div>
                    </div>
                    <div className="bet-slip__footer">
                        <BetSlipStake
                            focus="risk"
                            onFocusChange={() => {}}
                            risk={totalBet}
                            toWin={totalReward || ''}
                            onRiskChange={handleTotalBetChange}
                            showInputs
                            riskOnly
                            parlayLegs={bets.length}
                            parlayOdds={teaserOddsLabel}
                            legsKind="TEASER"
                        />
                        {shouldShowDelayedMessage && (
                            <div className="delayed-message-marekts">
                                <span className='font-message-delayed'>*This bet may take a little longer .</span>
                                <span className='font-message-delayed'>Thank you for understanding!</span>
                            </div>
                        )}
                        <BetSlipActions
                            mode="teaser"
                            onClear={resetBetState}
                            onPlace={confirmedBet}
                            placeBusy={placingBet}
                        />
                    </div>
                </>
            )}

        </div>
    );
};

export default TeaserBets;